/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import * as moment from 'moment';
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { Maybe } from '@codegrade/maybe';
import * as utils from '@/utils';
import NumberInput from './Number';
import { makeInputValue, mapValue, valueProp } from './utils';
const TIME_PARTS = ['days', 'hours', 'minutes', 'seconds'];
const TIME_PARTS_SET = new Set(TIME_PARTS);
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function getAsPart(part) {
    return utils.concatString('as', utils.capitalize(part));
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function getDurationAs(duration, part) {
    return duration[getAsPart(part)]();
}
export default tsx.componentFactoryOf().create({
    name: 'timedelta-editor',
    props: {
        value: valueProp().required,
        enabledParts: p
            .ofType()
            .validator(x => x instanceof Set)
            .default(() => TIME_PARTS_SET),
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    data() {
        return {
            parts: {
                days: makeInputValue(0),
                hours: makeInputValue(0),
                minutes: makeInputValue(0),
                seconds: makeInputValue(0),
            },
        };
    },
    computed: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        allEnabledParts() {
            return TIME_PARTS.filter(part => this.enabledParts.has(part));
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        partsToUse() {
            return this.allEnabledParts.map(part => [part, this.parts[part]]);
        },
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    created() {
        this.resetParts();
    },
    watch: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        value() {
            this.resetParts();
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        enabledParts() {
            this.resetParts();
        },
    },
    methods: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        resetParts() {
            // We already have an error, so ignore any error input values.
            if (this.value.isLeft() && Object.values(this.partsToUse).some(([, x]) => x.isLeft())) {
                return;
            }
            this.parts = {
                days: this.maybeGetValue('days'),
                hours: this.maybeGetValue('hours'),
                minutes: this.maybeGetValue('minutes'),
                seconds: this.maybeGetValue('seconds'),
            };
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        getErrorMessage(errors) {
            return new Error(Maybe.catMaybes([...errors])
                .map(utils.attr('message'))
                .join(' '));
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        emitValue() {
            const toEmit = utils.eitherUtils
                .and(...this.partsToUse.map(([part, value]) => value.map(maybe => maybe.map(v => [part, v]))))
                .mapLeft(errs => this.getErrorMessage(errs))
                .map(maybeValues => utils.maybeUtils
                .any(maybeValues)
                .map(values => values.reduce((acc, [part, value]) => acc.add(value, part), moment.duration(0))));
            tsx.emitOn(this, 'onInput', toEmit);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        onInput(part) {
            return (val) => {
                this.parts = { ...this.parts, [part]: val };
                this.emitValue();
            };
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        getValue(duration, part) {
            const allEnabled = this.allEnabledParts;
            const isFirst = allEnabled.length > 0 && allEnabled[0] === part;
            const isLast = utils.last(allEnabled) === part;
            if (isFirst) {
                const res = getDurationAs(duration, part);
                if (isLast) {
                    return res;
                }
                return Math.floor(res);
            }
            else if (isLast) {
                const total = getDurationAs(duration, part);
                // This is always possible as we are not the first
                // element.
                const prefPart = allEnabled[allEnabled.length - 2];
                const toRemove = moment.duration(Math.floor(getDurationAs(duration, prefPart)), prefPart);
                return total - getDurationAs(toRemove, part);
            }
            else {
                return duration[part]();
            }
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        maybeGetValue(part) {
            return mapValue(this.value, dur => this.getValue(dur, part));
        },
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    render() {
        return (<div class="flex items-stretch gap-y-1 flex-wrap bg-form-control">
                {this.partsToUse.map(([opt, value]) => (<label key={opt} class="grow flex items-stretch" style={{
                    maxWidth: `${Math.floor((100 / this.enabledParts.size) * 100) / 100}%`,
                }}>
                        <NumberInput min={0} value={value} name={`The ${opt} part`} onInput={this.onInput(opt)} class={['grow min-w-10 flex-auto pr-2!', opt]}/>
                        <span class="shrink self-center px-2 cg-form-label label-large ">
                            {opt}
                        </span>
                    </label>))}
            </div>);
    },
});
